// ═══════════════════════════════════════════════════════
// Agreement PDF Template — shared with MD Panel
// v180.1.0: Copied from orella-md-panel pdfTemplates.js
// Renders the full Patient Care Agreement HTML
// ═══════════════════════════════════════════════════════
import { ORELLA_LOGO_BASE64 } from '../assets/logo'

const DELIVER_GREEN = '#10b981'
const MEDIUM_GREY = '#64748B'
const SMALL_LOGO_HTML = `<img src="${ORELLA_LOGO_BASE64}" alt="Orella" style="height: 18px;" onerror="this.outerHTML='<span style=\\'font-size:12pt;font-weight:700;color:${MEDIUM_GREY}\\'>orella</span>'" />`

const generateAgreementV1 = ({ patientName, patientDOB, patientSex, patientId, caseId, signatureData, signedDate, doctor = {} }) => {
  const physicianName = doctor?.firstName && doctor?.lastName 
    ? `${doctor.firstName} ${doctor.lastName}, ${doctor.credentials || 'MD'}` 
    : 'Rami Abdou, MD'
  const physicianEntity = doctor?.practiceName || 'Dr. Rami MD Med Corp'
  
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Patient Care Agreement - ${patientName}</title>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { 
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; 
          padding: 30px 40px; 
          background: #ffffff;
          color: #1e293b;
          line-height: 1.5;
          font-size: 12pt;
        }
        
        .document-header {
          padding-bottom: 12px;
          border-bottom: 2px solid #1e293b;
          margin-bottom: 16px;
        }
        .practice-name {
          font-size: 14pt;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 2px;
        }
        .practice-details {
          font-size: 10pt;
          color: #64748b;
          line-height: 1.4;
        }
        
        .document-title {
          text-align: center;
          margin-bottom: 14px;
        }
        .document-title h1 {
          font-size: 16pt;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
        }
        
        .patient-info-bar {
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          padding: 10px 14px;
          margin-bottom: 14px;
          display: flex;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 10px;
        }
        .patient-info-item {
          flex: 1;
          min-width: 100px;
        }
        .patient-info-label {
          font-size: 10pt;
          color: #64748b;
          text-transform: uppercase;
          font-weight: 600;
          margin-bottom: 1px;
        }
        .patient-info-value {
          font-size: 12pt;
          color: #1e293b;
          font-weight: 600;
        }
        
        .highlights-box {
          background: #fefce8;
          border: 2px solid #eab308;
          border-radius: 8px;
          padding: 14px 16px;
          margin-bottom: 16px;
        }
        .highlights-title {
          font-size: 11pt;
          font-weight: 700;
          color: #854d0e;
          margin-bottom: 10px;
          text-transform: uppercase;
        }
        .highlights-section {
          margin-bottom: 10px;
        }
        .highlights-section-title {
          font-size: 11pt;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 3px;
        }
        .highlights-text {
          font-size: 12pt;
          color: #475569;
          line-height: 1.4;
        }
        .highlights-list {
          margin: 2px 0 2px 14px;
          font-size: 12pt;
          color: #475569;
        }
        .highlights-list li { margin-bottom: 1px; }
        
        .agreement-section { 
          margin-bottom: 12px; 
          page-break-inside: avoid; 
        }
        .section-header {
          font-size: 11pt;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 4px;
          border-bottom: 1px solid #e2e8f0;
          padding-bottom: 2px;
        }
        .agreement-text {
          font-size: 12pt;
          line-height: 1.5;
          color: #475569;
          text-align: justify;
          margin-bottom: 6px;
        }
        .agreement-list {
          margin: 4px 0 4px 18px;
          font-size: 12pt;
          color: #475569;
        }
        .agreement-list li { margin-bottom: 2px; }
        
        .highlight-box {
          padding: 8px 12px;
          border-radius: 5px;
          margin: 8px 0;
          font-size: 12pt;
        }
        .highlight-box.red { background: #fef2f2; border: 1px solid #ef4444; }
        .highlight-box.blue { background: #eff6ff; border: 1px solid #3b82f6; }
        
        .intro-text {
          font-size: 12pt;
          color: #475569;
          font-style: italic;
          margin-bottom: 12px;
        }
        
        .sig-box {
          background: #f0fdf4;
          border: 2px solid ${DELIVER_GREEN};
          border-radius: 8px;
          padding: 14px;
          text-align: center;
          margin-top: 16px;
        }
        .sig-image { max-height: 45px; margin-bottom: 4px; }
        
        .physician-block {
          margin-top: 14px;
          padding: 10px;
          background: #f8fafc;
          border-radius: 5px;
        }
        
        .document-footer {
          margin-top: 20px;
          padding-top: 10px;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 8pt;
          color: #64748b;
        }
        .footer-left {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        
        @media print {
          body { padding: 15px 25px; font-size: 8pt; }
          .agreement-section { page-break-inside: avoid; }
          .highlights-box { page-break-after: auto; }
        }
      </style>
    </head>
    <body>
      <!-- Header -->
      <div class="document-header">
        <div class="practice-name">${physicianEntity}</div>
        <div class="practice-details">
          700 S Flower Street, Suite 1000, Los Angeles, CA 90017<br>
          Tel: (323) 655-6222 | Fax: (323) 983-0888 | Email: admin@orellahealth.com
        </div>
      </div>
      
      <div class="document-title">
        <h1>PATIENT CARE AGREEMENT</h1>
      </div>
      
      <!-- Patient Info Bar -->
      <div class="patient-info-bar">
        <div class="patient-info-item">
          <div class="patient-info-label">Patient Name</div>
          <div class="patient-info-value">${patientName}</div>
        </div>
        <div class="patient-info-item">
          <div class="patient-info-label">Date of Birth</div>
          <div class="patient-info-value">${patientDOB || 'N/A'}</div>
        </div>
        <div class="patient-info-item">
          <div class="patient-info-label">Sex</div>
          <div class="patient-info-value">${patientSex || 'N/A'}</div>
        </div>
        <div class="patient-info-item">
          <div class="patient-info-label">ORL #</div>
          <div class="patient-info-value">${patientId || caseId?.slice(-8) || 'N/A'}</div>
        </div>
      </div>

      <!-- IMPORTANT HIGHLIGHTS -->
      <div class="highlights-box">
        <div class="highlights-title">⚠️ Important Highlights</div>
        
        <div class="highlights-section">
          <div class="highlights-section-title">COSTS & PAYMENT</div>
          <ul class="highlights-list">
            <li>Virtual consultation fee: $150 | In-office consultation with exam: $450</li>
            <li>Cancellation fee: $150 if canceled less than 24 hours in advance</li>
            <li>Late payment fee: 10% applied every 30 days</li>
            <li>You are responsible for costs not covered by insurance</li>
          </ul>
        </div>
        
        <div class="highlights-section">
          <div class="highlights-section-title">INSURANCE</div>
          <ul class="highlights-list">
            <li>PPO: No authorization required (copays/deductibles may apply)</li>
            <li>HMO: You must obtain a referral from your primary care physician</li>
            <li>Verify your coverage before your appointment</li>
          </ul>
        </div>
        
        <div class="highlights-section">
          <div class="highlights-section-title">COMMUNICATION & EMERGENCIES</div>
          <ul class="highlights-list">
            <li>General inquiries: admin@orellahealth.com or (323) 655-6222</li>
            <li><strong>Emergencies: Call 911 or go to the nearest ER — do not use email</strong></li>
            <li>We may use AI to assist with communications (you can opt out)</li>
          </ul>
        </div>
        
        <div class="highlights-section">
          <div class="highlights-section-title">YOUR PRIVACY RIGHTS</div>
          <ul class="highlights-list">
            <li>You can access and copy your medical records</li>
            <li>You can request restrictions on how we use your information</li>
            <li>You can request corrections to your records</li>
            <li>You can opt out of AI-assisted communications</li>
          </ul>
        </div>
        
        <div class="highlights-section">
          <div class="highlights-section-title">TERMINATION & DISPUTES</div>
          <ul class="highlights-list">
            <li>You may end this agreement anytime after a consultation</li>
            <li>We may terminate with 30 days notice or if balance exceeds $100</li>
            <li>Governed by California law; resolved through binding arbitration in Los Angeles County</li>
          </ul>
        </div>
      </div>

      <!-- FULL AGREEMENT -->
      <p class="intro-text">
        Hello! I am committed to providing you with the best possible care at the lowest possible cost. Please review the following key terms of my patient care agreement.
      </p>

      <!-- Section 1: Parties -->
      <div class="agreement-section">
        <div class="section-header">1. PARTIES</div>
        <p class="agreement-text">
          This Agreement is made between ${physicianEntity} ("Physician" or "Practice"), and the individual identified as the "Patient."
        </p>
        <p class="agreement-text">
          Orella Health, Inc. ("Orella") is a technology platform that facilitates the connection between Patients and independent Physicians. Orella is not the provider of medical services. The Physician is an independent medical professional who is solely responsible for the medical care provided to the Patient.
        </p>
      </div>

      <!-- Section 2: Services -->
      <div class="agreement-section">
        <div class="section-header">2. SERVICES</div>
        <p class="agreement-text">
          The Physician agrees to provide the Patient with medical and non-medical services ("Services") within the Physician's scope of practice. The nature and extent of Services will be determined through consultation between the Physician and Patient. These services include:
        </p>
        <ul class="agreement-list">
          <li><strong>Specialist Consultation and Examination:</strong> Subject to availability in your area.</li>
          <li><strong>Record of the Case:</strong> Including findings, assessment, and treatment recommendations.</li>
          <li><strong>Prescriptions:</strong> For any recommended/indicated studies, laboratory tests, medication, or consultations.</li>
        </ul>
        <p class="agreement-text">
          Please note that hospital, emergency, radiology, third-party, and laboratory services are not included unless offered in a cash package (at an additional cost).
        </p>
      </div>

      <!-- Section 3: Insurance -->
      <div class="agreement-section">
        <div class="section-header">3. INSURANCE</div>
        <p class="agreement-text">
          <strong>Authorization:</strong> To ensure your visit is covered by your insurance, please confirm with your insurance provider that you have authorization for the services being provided before your appointment. The specific procedure codes will depend on the nature of your visit.
        </p>
        <p class="agreement-text">
          <strong>PPO:</strong> No authorization is required, but you may be responsible for a co-payment and/or deductible as determined by your specific policy terms.
        </p>
        <p class="agreement-text">
          <strong>HMO:</strong> You must obtain a referral from your primary care physician before scheduling a consultation.
        </p>
        <p class="agreement-text">
          <strong>Patient Responsibility:</strong> While we will bill your insurance, you are ultimately responsible for all costs not covered by your insurance plan, including copayments, deductibles, and denied claims. We will provide you with the necessary documentation to submit to your insurance company for reimbursement.
        </p>
        <p class="agreement-text">
          <strong>Verification:</strong> It is your responsibility to verify insurance coverage for visits, procedures, and surgeries. We will provide all necessary records and assist you with the insurance appeals process if needed.
        </p>
      </div>

      <!-- Section 4: Fees and Payment -->
      <div class="agreement-section">
        <div class="section-header">4. FEES AND PAYMENT</div>
        <ul class="agreement-list">
          <li><strong>Virtual Consultation Fee:</strong> $150</li>
          <li><strong>In-Office Consultation with Exam:</strong> $450</li>
          <li><strong>Fee Schedule:</strong> A complete fee schedule is available upon request.</li>
          <li><strong>Payment:</strong> Payment is due in full upon receipt of an invoice from the Practice. We accept debit and credit cards.</li>
          <li><strong>Cancellation Fee:</strong> A non-refundable $150 fee may be charged for appointments canceled less than 24 hours in advance.</li>
          <li><strong>Late Payment:</strong> A 10% late fee is applied every 30 days. Please contact us if you are having difficulty with a payment.</li>
        </ul>
      </div>

      <!-- Section 5: Communications -->
      <div class="agreement-section">
        <div class="section-header">5. COMMUNICATIONS</div>
        <p class="agreement-text">
          <strong>Contact Information:</strong> Orella Health, 700 S Flower Street, Suite 1000, Los Angeles, CA 90017 | Phone: (323) 655-6222 | Fax: (323) 983-0888 | Email: admin@orellahealth.com
        </p>
        <p class="agreement-text">
          <strong>Preferred Methods:</strong> We encourage you to communicate with us via email for general inquiries. For sensitive information or medical records, we recommend using our secure patient portal.
        </p>
        <p class="agreement-text">
          <strong>Email Confidentiality:</strong> Please be aware that email communication is not entirely secure. We will make every effort to protect your privacy, but we cannot guarantee the confidentiality of information sent via email.
        </p>
        <div class="highlight-box red">
          <strong>EMERGENCIES:</strong> Email is not appropriate for emergencies or time-sensitive issues. In case of an emergency, please call 911 or go to the nearest emergency room.
        </div>
        <p class="agreement-text">
          <strong>GenAI Disclosure:</strong> Where applicable, communications may be generated with the assistance of GenAI, and you will be notified accordingly as outlined in Section 22.
        </p>
      </div>

      <!-- Section 6: Designated Physician -->
      <div class="agreement-section">
        <div class="section-header">6. DESIGNATED PHYSICIAN PROGRAM</div>
        <p class="agreement-text">
          Services will be personally provided by the Physician named in this Agreement. If the Physician is unavailable, an equally qualified covering physician or other licensed medical professional will be designated to attend to your medical care needs. They will have access to your medical history and course of care.
        </p>
      </div>

      <!-- Section 7: Appointment Scheduling -->
      <div class="agreement-section">
        <div class="section-header">7. APPOINTMENT SCHEDULING</div>
        <p class="agreement-text">
          Appointments are scheduled on a first-come, first-served basis, unless the Physician determines that a patient's medical condition requires urgent attention.
        </p>
      </div>

      <!-- Section 8: Term and Termination -->
      <div class="agreement-section">
        <div class="section-header">8. TERM AND TERMINATION</div>
        <p class="agreement-text">
          This Agreement remains in effect for as long as the Patient receives care from the Practice. This Agreement may be terminated by:
        </p>
        <p class="agreement-text">
          <strong>Patient:</strong> At any time following the completion of a consultation or immediately if the Patient relocates outside the Practice's service area.
        </p>
        <p class="agreement-text">
          <strong>Practice:</strong> (a) If the Patient breaches this Agreement and fails to cure the breach within ten days. (b) If the Patient has an outstanding balance of $100 or greater on their account and fails to pay within ten days of being requested to do so. (c) With 30 days prior written notice to the Patient, with or without cause. In this case, the Patient will be entitled to a refund of a prorated portion of any prepaid fees.
        </p>
        <p class="agreement-text">
          This Agreement automatically terminates upon the death or dissolution of either Party.
        </p>
      </div>

      <!-- Section 9: Dispute Resolution -->
      <div class="agreement-section">
        <div class="section-header">9. DISPUTE RESOLUTION</div>
        <p class="agreement-text">
          We are committed to resolving any concerns you may have. Please discuss any issues with our office staff first. If a resolution cannot be reached through discussion, we encourage the use of mediation. If mediation is unsuccessful, any disputes arising under this Agreement will be submitted to binding arbitration in accordance with the rules of the American Arbitration Association in Los Angeles County, California.
        </p>
      </div>

      <!-- Section 10: Independent Medical Judgment -->
      <div class="agreement-section">
        <div class="section-header">10. INDEPENDENT MEDICAL JUDGMENT</div>
        <p class="agreement-text">
          The Physician retains complete and free discretion to exercise their best professional medical judgment on your behalf. Nothing in this Agreement shall be deemed to influence, limit, or affect the Physician's independent medical judgment.
        </p>
      </div>

      <!-- Section 11: Terms of Usage -->
      <div class="agreement-section">
        <div class="section-header">11. TERMS OF USAGE</div>
        <p class="agreement-text">
          The Practice may designate specific Terms of Usage as a supplement to this Agreement by providing written notice to patients. In the event of any conflict, the Terms of Usage shall control.
        </p>
      </div>

      <!-- Section 12: Change of Law -->
      <div class="agreement-section">
        <div class="section-header">12. CHANGE OF LAW</div>
        <p class="agreement-text">
          If any change in state or federal law significantly impacts this Agreement, either party may request renegotiation. If an agreement cannot be reached within 30 days, either party may terminate this Agreement.
        </p>
      </div>

      <!-- Section 13: Severability -->
      <div class="agreement-section">
        <div class="section-header">13. SEVERABILITY</div>
        <p class="agreement-text">
          If any provision of this Agreement is deemed invalid or unenforceable, the remaining provisions shall remain in effect.
        </p>
      </div>

      <!-- Section 14: Notice -->
      <div class="agreement-section">
        <div class="section-header">14. NOTICE</div>
        <p class="agreement-text">
          Any written notice required under this Agreement shall be sent via facsimile, recognized overnight courier, or certified mail, return receipt requested, to the addresses indicated in this document.
        </p>
      </div>

      <!-- Section 15: Amendment -->
      <div class="agreement-section">
        <div class="section-header">15. AMENDMENT</div>
        <p class="agreement-text">
          This Agreement may be amended by a written agreement signed by both parties. The Physician may unilaterally amend this Agreement to comply with applicable laws and regulations by providing 30 days advance written notice to the Patient.
        </p>
      </div>

      <!-- Section 16: Assignment -->
      <div class="agreement-section">
        <div class="section-header">16. ASSIGNMENT</div>
        <p class="agreement-text">
          This Agreement may not be assigned to another individual by the Patient.
        </p>
      </div>

      <!-- Section 17: Legal Significance -->
      <div class="agreement-section">
        <div class="section-header">17. LEGAL SIGNIFICANCE</div>
        <p class="agreement-text">
          This Agreement is a legal document. You acknowledge that you have had the opportunity to seek legal advice and are satisfied with the terms and conditions.
        </p>
      </div>

      <!-- Section 18: Governing Law -->
      <div class="agreement-section">
        <div class="section-header">18. GOVERNING LAW/ARBITRATION</div>
        <p class="agreement-text">
          This Agreement shall be governed by the laws of the State of California. The parties waive any right to a trial by jury.
        </p>
      </div>

      <!-- Section 19: Waiver -->
      <div class="agreement-section">
        <div class="section-header">19. WAIVER</div>
        <p class="agreement-text">
          The failure of a party to enforce any provision of this Agreement shall not be deemed a waiver of that provision.
        </p>
      </div>

      <!-- Section 20: Service -->
      <div class="agreement-section">
        <div class="section-header">20. SERVICE</div>
        <p class="agreement-text">
          All written notices are deemed served if sent by first-class U.S. mail to the address on file with the Practice.
        </p>
      </div>

      <!-- Section 21: Entire Agreement -->
      <div class="agreement-section">
        <div class="section-header">21. ENTIRE AGREEMENT</div>
        <p class="agreement-text">
          This Agreement constitutes the entire agreement between the parties and supersedes any prior agreements or understandings.
        </p>
      </div>

      <!-- Section 22: GenAI -->
      <div class="agreement-section">
        <div class="section-header">22. GENERATIVE ARTIFICIAL INTELLIGENCE (GENAI)</div>
        <div class="highlight-box blue">
          <p class="agreement-text" style="margin: 0;">
            The Practice may utilize Generative Artificial Intelligence (GenAI) to assist in communicating patient clinical information. GenAI is a type of artificial intelligence that can create content, such as text or audio. When GenAI is used to create communications containing your clinical information, you will be notified at the beginning of the communication. This notification does not apply to communications that are reviewed by a licensed or certified human healthcare provider. <strong>You have the right to request that your clinical information not be used with GenAI. Please inform the Practice if you wish to opt out.</strong>
          </p>
        </div>
      </div>

      <!-- HIPAA Section -->
      <div class="agreement-section">
        <div class="section-header">HIPAA NOTICE OF PRIVACY PRACTICES</div>
        <p class="agreement-text" style="font-size: 8pt; color: #64748b;">Effective Date: March 1, 2025</p>
        <p class="agreement-text">
          <strong>THIS NOTICE DESCRIBES HOW MEDICAL INFORMATION ABOUT YOU MAY BE USED AND DISCLOSED AND HOW YOU CAN GET ACCESS TO THIS INFORMATION.</strong>
        </p>
        <p class="agreement-text">
          At Orella Health, we are committed to protecting your privacy. This Notice of Privacy Practices outlines how we may use and disclose your Protected Health Information (PHI) to carry out treatment, payment, or health care operations and for other purposes permitted or required by law.
        </p>
        <p class="agreement-text">
          <strong>How We May Use Your PHI:</strong> Treatment (coordinating care, referrals), Payment (obtaining payment, determining eligibility), Health Care Operations (quality improvement, training, business functions).
        </p>
        <p class="agreement-text">
          <strong>Your Rights:</strong> Right to inspect and copy your records; Right to request restrictions; Right to confidential communications; Right to amend your records; Right to an accounting of disclosures; Right to a paper copy of this Notice.
        </p>
        <p class="agreement-text">
          <strong>Filing a Complaint:</strong> If you believe your privacy rights have been violated, contact our Privacy Officer at admin@orellahealth.com or the U.S. Department of Health and Human Services. We will not retaliate against you for filing a complaint.
        </p>
      </div>

      <!-- Physician Signature Block -->
      <div class="physician-block">
        <p class="agreement-text" style="margin: 0;">
          <strong>${physicianName}</strong><br>
          ${physicianEntity}
        </p>
      </div>

      <!-- Patient Signature -->
      <div class="sig-box">
        <div style="font-size: 10pt; font-weight: 600; color: #16a34a; text-transform: uppercase; margin-bottom: 6px;">
          Patient Electronic Signature
        </div>
        ${signatureData 
          ? `<img src="${signatureData}" alt="Patient Signature" class="sig-image" />`
          : '<p style="color: #dc2626; font-size: 10pt;">Signature not available</p>'
        }
        <div style="font-size: 10pt; color: #15803d;">
          <strong>Signed:</strong> ${signedDate || 'N/A'}
        </div>
      </div>
      
      <!-- Footer -->
      <div class="document-footer">
        <div class="footer-left">
          <span>Powered by</span>
          ${SMALL_LOGO_HTML}
        </div>
        <div>Confidential Medical Information</div>
      </div>
    </body>
    </html>
  `
}

// ═══════════════════════════════════════════════════════
// v224.7.0 — Patient Care Agreement v2 (generalized template)
// Formal palette, no letterhead, practice identified in a
// Practice Information box + in §1. Fees come from the practice
// (doctor.virtualFee / doctor.inOfficeFee, or the API's
// self_pay_virtual_rate / self_pay_in_office_rate), DEFAULTING to $99/$249
// when the practice has set none; all other fees at provider
// discretion. Governing law / venue driven by
// the selected practice's state/city (CA today, any state).
// This block is IDENTICAL in orella-patient-app and orella-md-panel.
// ═══════════════════════════════════════════════════════
const generateAgreementV2 = ({ patientName, patientDOB, patientSex, patientId, caseId, signatureData, signedDate, doctor = {} }) => {
  const physicianName = (doctor && doctor.firstName && doctor.lastName)
    ? `${doctor.firstName} ${doctor.lastName}, ${doctor.credentials || 'MD'}`
    : (doctor && doctor.physicianName) || 'Rami Abdou, MD'
  const practiceName    = (doctor && doctor.practiceName) || 'Dr. Rami MD Med Corp'
  const practiceAddress = (doctor && doctor.practiceAddress) || '700 S Flower Street, Suite 1000, Los Angeles, CA 90017'
  const practicePhone   = (doctor && doctor.practicePhone) || ''
  const practiceFax     = (doctor && doctor.practiceFax) || ''
  const practiceEmail   = (doctor && doctor.practiceEmail) || ''
  const practiceState   = (doctor && doctor.practiceState) || 'California'
  const practiceCity    = (doctor && doctor.practiceCity) || ''
  // ★★★ TF b34 #8 — the consultation fees. These were three hardcoded literals
  //   ($99, $249) in a template whose every other practice-specific value was
  //   already data. A patient asked whether everyone has to pay them; the honest
  //   answer was yes, whoever the practice was.
  //   The rule is the practice's cash price, falling back to 99/249 when the
  //   practice has set nothing — so no existing agreement changes until an MD
  //   actually enters a number.
  //   ★ A fee of 0 is NOT a missing fee. `Number.isFinite(n) && n >= 0` is
  //     deliberate: a practice that offers a free virtual consult must be able
  //     to say so, and `|| 99` would silently overwrite it with a bill.
  //   ★ Cents are printed only when there are cents, so 99 stays "$99" and does
  //     not become "$99.00" in a document people have already read.
  const feeText = (raw, fallback) => {
    const n = Number(raw)
    if (raw === null || raw === undefined || raw === '' || !Number.isFinite(n) || n < 0) return fallback
    return '$' + (Number.isInteger(n) ? String(n) : n.toFixed(2))
  }
  // ★ v242.151.0 — the second name is the one the API actually sends
  //   (`attach_self_pay_to_case_doctor`, API v243.97.0). It used to be
  //   `virtual_consult_fee`, which nothing has ever written.
  const virtualFee  = feeText(doctor && (doctor.virtualFee  != null ? doctor.virtualFee  : doctor.self_pay_virtual_rate),    '$99')
  const inOfficeFee = feeText(doctor && (doctor.inOfficeFee != null ? doctor.inOfficeFee : doctor.self_pay_in_office_rate), '$249')
  const venue = practiceCity ? `${practiceCity}, ${practiceState}` : practiceState
  const orlId = patientId || (caseId ? caseId.slice(-8) : '') || 'N/A'
  const contactLine = [
    practicePhone && `Tel: ${practicePhone}`,
    practiceFax && `Fax: ${practiceFax}`,
    practiceEmail && `Email: ${practiceEmail}`,
  ].filter(Boolean).join(' &nbsp;&middot;&nbsp; ')
  const inquiryLine = [practiceEmail, practicePhone].filter(Boolean).join(' or ') || 'the contact details above'
  const TERMS_EFFECTIVE = 'June 1, 2026'

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Patient Care Agreement - ${patientName}</title>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; padding: 32px 44px; background: #ffffff; color: #26313b; line-height: 1.55; font-size: 12pt; }
        .document-title { text-align: center; margin: 4px 0 6px; }
        .document-title h1 { font-size: 17pt; font-weight: 700; color: #15354F; letter-spacing: 1px; }
        .title-rule { height: 2px; background: #15354F; width: 100%; margin: 0 0 16px; }
        .info-box { background: #F7F9FB; border: 1px solid #D6DCE2; border-radius: 4px; padding: 11px 14px; margin-bottom: 10px; }
        .info-row { display: flex; justify-content: space-between; flex-wrap: wrap; gap: 10px; }
        .info-item { flex: 1; min-width: 120px; }
        .info-label { font-size: 9pt; color: #5b6873; text-transform: uppercase; font-weight: 600; letter-spacing: .4px; margin-bottom: 1px; }
        .info-value { font-size: 12pt; color: #15354F; font-weight: 700; }
        .practice-box { background: #F7F9FB; border: 1px solid #D6DCE2; border-radius: 4px; padding: 12px 16px; margin-bottom: 18px; }
        .practice-cap { font-size: 9pt; text-transform: uppercase; letter-spacing: .5px; color: #5b6873; font-weight: 700; margin-bottom: 8px; }
        .pb-grid { display: grid; grid-template-columns: 155px 1fr; row-gap: 5px; column-gap: 14px; font-size: 11pt; }
        .pb-label { color: #5b6873; font-weight: 600; }
        .pb-value { color: #15354F; font-weight: 600; }
        .highlights-box { background: #F7F9FB; border: 1px solid #D6DCE2; border-radius: 4px; padding: 14px 16px 8px; margin-bottom: 18px; }
        .highlights-title { font-size: 11pt; font-weight: 700; color: #15354F; margin-bottom: 10px; text-transform: uppercase; letter-spacing: .4px; }
        .hl-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 26px; }
        .hl-section { margin-bottom: 10px; }
        .hl-section-title { font-size: 10.5pt; font-weight: 700; color: #15354F; margin-bottom: 3px; }
        .hl-list { margin: 2px 0 2px 16px; font-size: 10.5pt; color: #3b4754; }
        .hl-list li { margin-bottom: 2px; }
        .intro-text { font-size: 12pt; color: #3b4754; margin-bottom: 14px; }
        .intro-text .hello { font-weight: 700; color: #15354F; }
        .agreement-section { margin-bottom: 13px; page-break-inside: avoid; }
        .section-header { font-size: 11pt; font-weight: 700; color: #15354F; margin-bottom: 4px; border-bottom: 1px solid #E2E8F0; padding-bottom: 2px; }
        .agreement-text { font-size: 11.5pt; line-height: 1.55; color: #3b4754; margin-bottom: 6px; text-align: justify; }
        .agreement-text strong { color: #334155; }
        .agreement-list { margin: 4px 0 6px 20px; font-size: 11.5pt; color: #3b4754; }
        .agreement-list li { margin-bottom: 3px; }
        .callout { padding: 8px 12px; border-radius: 4px; margin: 8px 0; font-size: 11.5pt; background: #F7F9FB; border: 1px solid #D6DCE2; color: #3b4754; }
        .callout.emergency { border-left: 3px solid #9a3a3a; }
        .callout.emergency strong { color: #9a3a3a; }
        .fees-line { font-size: 12pt; font-weight: 700; color: #334155; margin-bottom: 8px; }
        .issued-by { margin-top: 16px; padding-top: 10px; border-top: 1px solid #E2E8F0; }
        .issued-by .label { font-size: 9pt; text-transform: uppercase; letter-spacing: .5px; color: #5b6873; font-weight: 600; margin-bottom: 4px; }
        .issued-by .name { font-weight: 700; color: #15354F; }
        .sig-box { background: #F7F9FB; border: 1px solid #D6DCE2; border-top: 2px solid #15354F; border-radius: 4px; padding: 14px 16px; margin-top: 16px; }
        .sig-title { font-size: 10.5pt; font-weight: 700; color: #15354F; text-transform: uppercase; margin-bottom: 6px; letter-spacing: .4px; }
        .sig-ack { font-size: 10.5pt; color: #3b4754; margin-bottom: 10px; }
        .sig-image { max-height: 48px; margin-bottom: 4px; }
        .sig-line { font-size: 10.5pt; color: #26313b; }
        .version-stamp { margin-top: 10px; font-size: 8.5pt; color: #94A3B8; text-align: center; }
        .document-footer { margin-top: 22px; padding-top: 10px; border-top: 1px solid #E2E8F0; display: flex; justify-content: space-between; align-items: center; font-size: 8.5pt; color: #5b6873; }
        .footer-left { display: flex; align-items: center; gap: 7px; }
        @media print { body { padding: 18px 26px; } .agreement-section { page-break-inside: avoid; } }
      </style>
    </head>
    <body>
      <div class="document-title"><h1>PATIENT CARE AGREEMENT</h1></div>
      <div class="title-rule"></div>

      <div class="info-box"><div class="info-row">
        <div class="info-item"><div class="info-label">Patient Name</div><div class="info-value">${patientName}</div></div>
        <div class="info-item"><div class="info-label">Date of Birth</div><div class="info-value">${patientDOB || 'N/A'}</div></div>
        <div class="info-item"><div class="info-label">Sex</div><div class="info-value">${patientSex || 'N/A'}</div></div>
        <div class="info-item"><div class="info-label">ORL #</div><div class="info-value">${orlId}</div></div>
      </div></div>

      <div class="practice-box">
        <div class="practice-cap">Practice Information</div>
        <div class="pb-grid">
          <div class="pb-label">Practice</div><div class="pb-value">${practiceName}</div>
          <div class="pb-label">Treating Physician</div><div class="pb-value">${physicianName}</div>
          <div class="pb-label">Address</div><div class="pb-value">${practiceAddress}</div>
          ${contactLine ? `<div class="pb-label">Contact</div><div class="pb-value">${contactLine}</div>` : ''}
        </div>
      </div>

      <div class="highlights-box">
        <div class="highlights-title">Important Highlights</div>
        <div class="hl-grid">
          <div class="hl-section"><div class="hl-section-title">COSTS &amp; PAYMENT</div><ul class="hl-list">
            <li>Virtual consultation: ${virtualFee}</li>
            <li>In-office consultation with exam: ${inOfficeFee}</li>
            <li>Fees cover the consultation only &mdash; not other services or procedures</li>
            <li>All other fees are at the discretion of the servicing provider</li>
            <li>You are responsible for costs not covered by insurance</li>
          </ul></div>
          <div class="hl-section"><div class="hl-section-title">INSURANCE</div><ul class="hl-list">
            <li>PPO: no authorization required (copays/deductibles may apply)</li>
            <li>HMO: obtain a referral from your primary care physician</li>
            <li>Verify your coverage before your appointment</li>
          </ul></div>
          <div class="hl-section"><div class="hl-section-title">COMMUNICATION &amp; EMERGENCIES</div><ul class="hl-list">
            <li>General inquiries: ${inquiryLine}</li>
            <li><strong>Emergencies: call 911 or go to the nearest ER &mdash; do not use email</strong></li>
            <li>AI may assist with communications (you can opt out)</li>
          </ul></div>
          <div class="hl-section"><div class="hl-section-title">YOUR PRIVACY &amp; TERMINATION</div><ul class="hl-list">
            <li>Access, copy, and request corrections to your records</li>
            <li>Request restrictions on use of your information</li>
            <li>Opt out of AI-assisted communications</li>
            <li>Either party may end the relationship per Section 9</li>
            <li>Governed by ${practiceState} law; arbitration in ${venue}</li>
          </ul></div>
        </div>
      </div>

      <p class="intro-text"><span class="hello">Hello!</span> Your treating physician, ${physicianName} of ${practiceName}, is committed to providing you with high-quality care. Please review the key terms of this Patient Care Agreement below.</p>

      <div class="agreement-section"><div class="section-header">1. PARTIES</div>
        <p class="agreement-text">This Agreement is entered into between <strong>${practiceName}</strong>, together with the treating physician <strong>${physicianName}</strong>, whose practice details are set out above (collectively, the "Physician" or "Practice"), and the individual identified above as the "Patient." The Physician is an independent, licensed medical professional who is solely responsible for all medical care provided to the Patient. This Agreement governs the professional relationship between the Patient and the Physician.</p>
      </div>

      <div class="agreement-section"><div class="section-header">2. ROLE OF ORELLA (TECHNOLOGY PLATFORM)</div>
        <p class="agreement-text">Orella, Inc. ("Orella") operates a technology platform that facilitates the connection between patients and independent physicians and supports administrative functions such as scheduling, intake, documentation, payments, and communications. <strong>Orella is not a healthcare provider and does not practice medicine, render diagnoses, or provide treatment. Orella does not employ, supervise, or direct the Physician and exercises no control over the Physician's independent clinical judgment.</strong> All medical care is provided solely by the Physician. Orella is not a party to the physician-patient relationship and, to the maximum extent permitted by law, assumes no responsibility or liability for the medical services provided by the Physician.</p>
      </div>

      <div class="agreement-section"><div class="section-header">3. SERVICES</div>
        <p class="agreement-text">The Physician agrees to provide the Patient with medical and non-medical services ("Services") within the Physician's scope of practice. The nature and extent of Services will be determined through consultation between the Physician and Patient, and may include:</p>
        <ul class="agreement-list">
          <li><strong>Specialist Consultation and Examination:</strong> subject to availability in your area.</li>
          <li><strong>Record of the Case:</strong> including findings, assessment, and treatment recommendations.</li>
          <li><strong>Prescriptions:</strong> for recommended or indicated studies, laboratory tests, medication, or consultations.</li>
        </ul>
        <p class="agreement-text">Hospital, emergency, radiology, third-party, and laboratory services are not included unless offered as a cash package at additional cost.</p>
      </div>

      <div class="agreement-section"><div class="section-header">4. FEES AND PAYMENT</div>
        <p class="fees-line">Virtual Consultation Fee: ${virtualFee} &nbsp;&nbsp;&nbsp; In-Office Consultation with Exam: ${inOfficeFee}</p>
        <p class="agreement-text">These consultation fees cover the consultation only and do not include any other services, procedures, surgeries, laboratory work, imaging, or third-party costs. All other fees are set at the discretion of the servicing provider. A complete fee schedule is available upon request.</p>
        <ul class="agreement-list">
          <li><strong>Payment:</strong> due in full upon receipt of an invoice from the Practice. Debit and credit cards are accepted.</li>
          <li><strong>Cancellations:</strong> a fee may apply for appointments canceled with less than 24 hours' notice or for missed appointments, at the discretion of the servicing provider.</li>
          <li><strong>Late Payment:</strong> overdue balances may be subject to a late fee, at the discretion of the servicing provider. Please contact the Practice if you are having difficulty with a payment.</li>
        </ul>
      </div>

      <div class="agreement-section"><div class="section-header">5. INSURANCE</div>
        <p class="agreement-text"><strong>Authorization:</strong> To help ensure your visit is covered, please confirm with your insurance provider that you have authorization for the services being provided before your appointment. The specific procedure codes depend on the nature of your visit.</p>
        <p class="agreement-text"><strong>PPO:</strong> No authorization is required, but you may be responsible for a co-payment and/or deductible per your policy.</p>
        <p class="agreement-text"><strong>HMO:</strong> You must obtain a referral from your primary care physician before scheduling a consultation.</p>
        <p class="agreement-text"><strong>Patient Responsibility:</strong> While the Practice will bill your insurance, you are ultimately responsible for all costs not covered by your plan, including copayments, deductibles, and denied claims. The Practice will provide the documentation needed to submit to your insurer for reimbursement and will assist with the appeals process if needed. It is your responsibility to verify coverage for visits, procedures, and surgeries.</p>
      </div>

      <div class="agreement-section"><div class="section-header">6. COMMUNICATIONS</div>
        <p class="agreement-text"><strong>Contact:</strong> ${practiceName}, ${practiceAddress}${contactLine ? ` &nbsp;|&nbsp; ${contactLine}` : ''}</p>
        <p class="agreement-text"><strong>Preferred Methods:</strong> Please use email for general inquiries and the secure patient portal for sensitive information or medical records.</p>
        <p class="agreement-text"><strong>Email Confidentiality:</strong> Email is not entirely secure. The Practice will make every effort to protect your privacy but cannot guarantee the confidentiality of information sent via email.</p>
        <div class="callout emergency"><strong>EMERGENCIES:</strong> Email is not appropriate for emergencies or time-sensitive issues. In an emergency, call 911 or go to the nearest emergency room.</div>
        <p class="agreement-text"><strong>GenAI Disclosure:</strong> Where applicable, communications may be generated with the assistance of GenAI, and you will be notified as outlined in Section 23.</p>
      </div>

      <div class="agreement-section"><div class="section-header">7. DESIGNATED PHYSICIAN PROGRAM</div>
        <p class="agreement-text">Services will be personally provided by the Physician named in this Agreement. If the Physician is unavailable, an equally qualified covering physician or other licensed professional will be designated to attend to your care needs and will have access to your medical history and course of care.</p>
      </div>

      <div class="agreement-section"><div class="section-header">8. APPOINTMENT SCHEDULING</div>
        <p class="agreement-text">Appointments are scheduled on a first-come, first-served basis, unless the Physician determines that a patient's medical condition requires urgent attention.</p>
      </div>

      <div class="agreement-section"><div class="section-header">9. TERM AND TERMINATION</div>
        <p class="agreement-text">This Agreement remains in effect for as long as the Patient receives care from the Practice. It may be terminated as follows:</p>
        <p class="agreement-text"><strong>Patient:</strong> at any time following the completion of a consultation, or immediately if the Patient relocates outside the Practice's service area.</p>
        <p class="agreement-text"><strong>Practice:</strong> (a) if the Patient breaches this Agreement and fails to cure within ten days; (b) if the Patient carries an outstanding balance of $100 or greater and fails to pay within ten days of request; or (c) with thirty days' prior written notice, with or without cause (prepaid fees prorated and refunded). This Agreement automatically terminates upon the death or dissolution of either party.</p>
      </div>

      <div class="agreement-section"><div class="section-header">10. DISPUTE RESOLUTION</div>
        <p class="agreement-text">Please raise any concerns with the Practice first. If a resolution cannot be reached, the parties encourage mediation. If mediation is unsuccessful, any dispute arising under this Agreement will be submitted to binding arbitration under the rules of the American Arbitration Association in ${venue}.</p>
      </div>

      <div class="agreement-section"><div class="section-header">11. INDEPENDENT MEDICAL JUDGMENT</div>
        <p class="agreement-text">The Physician retains complete and free discretion to exercise their best professional medical judgment on the Patient's behalf. Nothing in this Agreement &mdash; and no service, tool, or communication provided through the Orella platform &mdash; shall be deemed to influence, limit, or affect the Physician's independent medical judgment.</p>
      </div>

      <div class="agreement-section"><div class="section-header">12. TERMS OF USAGE</div>
        <p class="agreement-text">The Practice may designate specific Terms of Usage as a supplement to this Agreement by providing written notice. In the event of any conflict, the Terms of Usage shall control.</p>
      </div>

      <div class="agreement-section"><div class="section-header">13. CHANGE OF LAW</div>
        <p class="agreement-text">If any change in state or federal law significantly impacts this Agreement, either party may request renegotiation. If an agreement cannot be reached within 30 days, either party may terminate this Agreement.</p>
      </div>

      <div class="agreement-section"><div class="section-header">14. SEVERABILITY</div>
        <p class="agreement-text">If any provision of this Agreement is deemed invalid or unenforceable, the remaining provisions shall remain in effect.</p>
      </div>

      <div class="agreement-section"><div class="section-header">15. NOTICE</div>
        <p class="agreement-text">Any written notice required under this Agreement shall be sent via facsimile, recognized overnight courier, or certified mail, return receipt requested, to the addresses indicated in this document.</p>
      </div>

      <div class="agreement-section"><div class="section-header">16. AMENDMENT</div>
        <p class="agreement-text">This Agreement may be amended by a written agreement signed by both parties. The Physician may unilaterally amend this Agreement to comply with applicable laws and regulations by providing 30 days' advance written notice to the Patient.</p>
      </div>

      <div class="agreement-section"><div class="section-header">17. ASSIGNMENT</div>
        <p class="agreement-text">This Agreement may not be assigned to another individual by the Patient.</p>
      </div>

      <div class="agreement-section"><div class="section-header">18. LEGAL SIGNIFICANCE</div>
        <p class="agreement-text">This Agreement is a legal document. You acknowledge that you have had the opportunity to seek legal advice and are satisfied with the terms and conditions.</p>
      </div>

      <div class="agreement-section"><div class="section-header">19. GOVERNING LAW / ARBITRATION</div>
        <p class="agreement-text">This Agreement shall be governed by the laws of the State of ${practiceState}. The parties waive any right to a trial by jury.</p>
      </div>

      <div class="agreement-section"><div class="section-header">20. WAIVER</div>
        <p class="agreement-text">The failure of a party to enforce any provision of this Agreement shall not be deemed a waiver of that provision.</p>
      </div>

      <div class="agreement-section"><div class="section-header">21. SERVICE</div>
        <p class="agreement-text">All written notices are deemed served if sent by first-class U.S. mail to the address on file with the Practice.</p>
      </div>

      <div class="agreement-section"><div class="section-header">22. ENTIRE AGREEMENT</div>
        <p class="agreement-text">This Agreement constitutes the entire agreement between the parties and supersedes any prior agreements or understandings regarding its subject matter.</p>
      </div>

      <div class="agreement-section"><div class="section-header">23. GENERATIVE ARTIFICIAL INTELLIGENCE (GENAI)</div>
        <div class="callout">
          <p class="agreement-text" style="margin: 0;">The Practice may utilize Generative Artificial Intelligence (GenAI) to assist in communicating patient clinical information. When GenAI is used to create communications containing your clinical information, you will be notified at the beginning of the communication. This notification does not apply to communications reviewed by a licensed or certified human healthcare provider. <strong>You have the right to request that your clinical information not be used with GenAI. Please inform the Practice if you wish to opt out.</strong></p>
        </div>
      </div>

      <div class="agreement-section"><div class="section-header">HIPAA NOTICE OF PRIVACY PRACTICES</div>
        <p class="agreement-text" style="font-size: 8.5pt; color: #5b6873;">Effective Date: March 1, 2025</p>
        <p class="agreement-text"><strong>THIS NOTICE DESCRIBES HOW MEDICAL INFORMATION ABOUT YOU MAY BE USED AND DISCLOSED AND HOW YOU CAN GET ACCESS TO THIS INFORMATION.</strong></p>
        <p class="agreement-text">The Practice is committed to protecting your privacy. This Notice outlines how ${practiceName} may use and disclose your Protected Health Information (PHI) to carry out treatment, payment, or health care operations and for other purposes permitted or required by law.</p>
        <p class="agreement-text"><strong>How We May Use Your PHI:</strong> Treatment (coordinating care, referrals), Payment (obtaining payment, determining eligibility), Health Care Operations (quality improvement, training, business functions).</p>
        <p class="agreement-text"><strong>Your Rights:</strong> to inspect and copy your records; to request restrictions; to confidential communications; to amend your records; to an accounting of disclosures; and to a paper copy of this Notice.</p>
        <p class="agreement-text"><strong>Filing a Complaint:</strong> If you believe your privacy rights have been violated, contact the Practice's Privacy Officer at ${practiceEmail || 'the Practice'} or the U.S. Department of Health and Human Services. The Practice will not retaliate against you for filing a complaint.</p>
      </div>

      <div class="issued-by">
        <div class="label">Issued by</div>
        <div class="name">${physicianName}</div>
        <div>${practiceName}</div>
      </div>

      <div class="sig-box">
        <div class="sig-title">Patient Acknowledgement &amp; Electronic Signature</div>
        <div class="sig-ack">By signing below, I acknowledge that I have read, understand, and agree to the terms of this Patient Care Agreement, and I acknowledge receipt of the Notice of Privacy Practices.</div>
        ${signatureData
          ? `<img src="${signatureData}" alt="Patient Signature" class="sig-image" />`
          : '<p style="color:#9a3a3a;font-size:10pt;">Signature pending</p>'}
        <div class="sig-line"><strong>Patient:</strong> ${patientName} &nbsp;&nbsp; <strong>Signed:</strong> ${signedDate || 'N/A'}</div>
      </div>

      <div class="version-stamp">Patient Care Agreement v2.0 &middot; Terms effective ${TERMS_EFFECTIVE} &middot; Signature bound to this version</div>

      <div class="document-footer">
        <div class="footer-left"><span>Powered by</span>${SMALL_LOGO_HTML}</div>
        <div>Confidential Medical Information</div>
      </div>
    </body>
    </html>
  `
}

// ═══════════════════════════════════════════════════════
// v242.239.0 — Patient Care Agreement v3 (R32 · TEST 2.21, patient P2-5).
// The v2 text with no fees and no pricing: Dr. Rami ruled the agreement covers
// Orella's role only (each practice sets its own prices). No COSTS & PAYMENT
// highlights, no section 4 FEES AND PAYMENT (later sections moved up by one),
// no virtualFee / inOfficeFee, no $99 / $249 fallback, no "lowest possible cost"
// (Alex, PROD-679). Terms that are not prices stay: the $100 balance
// termination rule, "responsible for all costs not covered", "cash package at
// additional cost".
// ★★★ NEW SIGNATURES ONLY. V1 and V2 above stay byte-identical (a signed
// agreement is never revised): a record renders the version it carries.
// CURRENT_AGREEMENT_VERSION is what a new app signature records, and
// CURRENT_HARDCODED_TEMPLATE ('v3.0') is the marker that says the signature is
// of THIS hardcoded text (see the dispatcher below for why the number alone
// cannot say it).
// ═══════════════════════════════════════════════════════
const generateAgreementV3 = ({ patientName, patientDOB, patientSex, patientId, caseId, signatureData, signedDate, doctor = {} }) => {
  const physicianName = (doctor && doctor.firstName && doctor.lastName)
    ? `${doctor.firstName} ${doctor.lastName}, ${doctor.credentials || 'MD'}`
    : (doctor && doctor.physicianName) || 'Rami Abdou, MD'
  const practiceName    = (doctor && doctor.practiceName) || 'Dr. Rami MD Med Corp'
  const practiceAddress = (doctor && doctor.practiceAddress) || '700 S Flower Street, Suite 1000, Los Angeles, CA 90017'
  const practicePhone   = (doctor && doctor.practicePhone) || ''
  const practiceFax     = (doctor && doctor.practiceFax) || ''
  const practiceEmail   = (doctor && doctor.practiceEmail) || ''
  const practiceState   = (doctor && doctor.practiceState) || 'California'
  const practiceCity    = (doctor && doctor.practiceCity) || ''
  // ★★★ R32 · TEST 2.21 (patient P2-5, v242.239.0): NO FEES IN THIS AGREEMENT. Dr. Rami ruled
  //   that the Patient Care Agreement covers Orella's role only and carries no pricing (each
  //   practice sets its own prices; counsel confirms it need not carry pricing, GFE or no-show
  //   terms). feeText / virtualFee / inOfficeFee, the $99 / $249 fallback, the COSTS & PAYMENT
  //   highlights and section 4 (FEES AND PAYMENT) are gone; later sections moved up by one.
  const venue = practiceCity ? `${practiceCity}, ${practiceState}` : practiceState
  const orlId = patientId || (caseId ? caseId.slice(-8) : '') || 'N/A'
  const contactLine = [
    practicePhone && `Tel: ${practicePhone}`,
    practiceFax && `Fax: ${practiceFax}`,
    practiceEmail && `Email: ${practiceEmail}`,
  ].filter(Boolean).join(' &nbsp;&middot;&nbsp; ')
  const inquiryLine = [practiceEmail, practicePhone].filter(Boolean).join(' or ') || 'the contact details above'
  const TERMS_EFFECTIVE = 'June 1, 2026'

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Patient Care Agreement - ${patientName}</title>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; padding: 32px 44px; background: #ffffff; color: #26313b; line-height: 1.55; font-size: 12pt; }
        .document-title { text-align: center; margin: 4px 0 6px; }
        .document-title h1 { font-size: 17pt; font-weight: 700; color: #15354F; letter-spacing: 1px; }
        .title-rule { height: 2px; background: #15354F; width: 100%; margin: 0 0 16px; }
        .info-box { background: #F7F9FB; border: 1px solid #D6DCE2; border-radius: 4px; padding: 11px 14px; margin-bottom: 10px; }
        .info-row { display: flex; justify-content: space-between; flex-wrap: wrap; gap: 10px; }
        .info-item { flex: 1; min-width: 120px; }
        .info-label { font-size: 9pt; color: #5b6873; text-transform: uppercase; font-weight: 600; letter-spacing: .4px; margin-bottom: 1px; }
        .info-value { font-size: 12pt; color: #15354F; font-weight: 700; }
        .practice-box { background: #F7F9FB; border: 1px solid #D6DCE2; border-radius: 4px; padding: 12px 16px; margin-bottom: 18px; }
        .practice-cap { font-size: 9pt; text-transform: uppercase; letter-spacing: .5px; color: #5b6873; font-weight: 700; margin-bottom: 8px; }
        .pb-grid { display: grid; grid-template-columns: 155px 1fr; row-gap: 5px; column-gap: 14px; font-size: 11pt; }
        .pb-label { color: #5b6873; font-weight: 600; }
        .pb-value { color: #15354F; font-weight: 600; }
        .highlights-box { background: #F7F9FB; border: 1px solid #D6DCE2; border-radius: 4px; padding: 14px 16px 8px; margin-bottom: 18px; }
        .highlights-title { font-size: 11pt; font-weight: 700; color: #15354F; margin-bottom: 10px; text-transform: uppercase; letter-spacing: .4px; }
        .hl-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 26px; }
        .hl-section { margin-bottom: 10px; }
        .hl-section-title { font-size: 10.5pt; font-weight: 700; color: #15354F; margin-bottom: 3px; }
        .hl-list { margin: 2px 0 2px 16px; font-size: 10.5pt; color: #3b4754; }
        .hl-list li { margin-bottom: 2px; }
        .intro-text { font-size: 12pt; color: #3b4754; margin-bottom: 14px; }
        .intro-text .hello { font-weight: 700; color: #15354F; }
        .agreement-section { margin-bottom: 13px; page-break-inside: avoid; }
        .section-header { font-size: 11pt; font-weight: 700; color: #15354F; margin-bottom: 4px; border-bottom: 1px solid #E2E8F0; padding-bottom: 2px; }
        .agreement-text { font-size: 11.5pt; line-height: 1.55; color: #3b4754; margin-bottom: 6px; text-align: justify; }
        .agreement-text strong { color: #334155; }
        .agreement-list { margin: 4px 0 6px 20px; font-size: 11.5pt; color: #3b4754; }
        .agreement-list li { margin-bottom: 3px; }
        .callout { padding: 8px 12px; border-radius: 4px; margin: 8px 0; font-size: 11.5pt; background: #F7F9FB; border: 1px solid #D6DCE2; color: #3b4754; }
        .callout.emergency { border-left: 3px solid #9a3a3a; }
        .callout.emergency strong { color: #9a3a3a; }
        .issued-by { margin-top: 16px; padding-top: 10px; border-top: 1px solid #E2E8F0; }
        .issued-by .label { font-size: 9pt; text-transform: uppercase; letter-spacing: .5px; color: #5b6873; font-weight: 600; margin-bottom: 4px; }
        .issued-by .name { font-weight: 700; color: #15354F; }
        .sig-box { background: #F7F9FB; border: 1px solid #D6DCE2; border-top: 2px solid #15354F; border-radius: 4px; padding: 14px 16px; margin-top: 16px; }
        .sig-title { font-size: 10.5pt; font-weight: 700; color: #15354F; text-transform: uppercase; margin-bottom: 6px; letter-spacing: .4px; }
        .sig-ack { font-size: 10.5pt; color: #3b4754; margin-bottom: 10px; }
        .sig-image { max-height: 48px; margin-bottom: 4px; }
        .sig-line { font-size: 10.5pt; color: #26313b; }
        .version-stamp { margin-top: 10px; font-size: 8.5pt; color: #94A3B8; text-align: center; }
        .document-footer { margin-top: 22px; padding-top: 10px; border-top: 1px solid #E2E8F0; display: flex; justify-content: space-between; align-items: center; font-size: 8.5pt; color: #5b6873; }
        .footer-left { display: flex; align-items: center; gap: 7px; }
        @media print { body { padding: 18px 26px; } .agreement-section { page-break-inside: avoid; } }
      </style>
    </head>
    <body>
      <div class="document-title"><h1>PATIENT CARE AGREEMENT</h1></div>
      <div class="title-rule"></div>

      <div class="info-box"><div class="info-row">
        <div class="info-item"><div class="info-label">Patient Name</div><div class="info-value">${patientName}</div></div>
        <div class="info-item"><div class="info-label">Date of Birth</div><div class="info-value">${patientDOB || 'N/A'}</div></div>
        <div class="info-item"><div class="info-label">Sex</div><div class="info-value">${patientSex || 'N/A'}</div></div>
        <div class="info-item"><div class="info-label">ORL #</div><div class="info-value">${orlId}</div></div>
      </div></div>

      <div class="practice-box">
        <div class="practice-cap">Practice Information</div>
        <div class="pb-grid">
          <div class="pb-label">Practice</div><div class="pb-value">${practiceName}</div>
          <div class="pb-label">Treating Physician</div><div class="pb-value">${physicianName}</div>
          <div class="pb-label">Address</div><div class="pb-value">${practiceAddress}</div>
          ${contactLine ? `<div class="pb-label">Contact</div><div class="pb-value">${contactLine}</div>` : ''}
        </div>
      </div>

      <div class="highlights-box">
        <div class="highlights-title">Important Highlights</div>
        <div class="hl-grid">
          <div class="hl-section"><div class="hl-section-title">INSURANCE</div><ul class="hl-list">
            <li>PPO: no authorization required (copays/deductibles may apply)</li>
            <li>HMO: obtain a referral from your primary care physician</li>
            <li>Verify your coverage before your appointment</li>
          </ul></div>
          <div class="hl-section"><div class="hl-section-title">COMMUNICATION &amp; EMERGENCIES</div><ul class="hl-list">
            <li>General inquiries: ${inquiryLine}</li>
            <li><strong>Emergencies: call 911 or go to the nearest ER &mdash; do not use email</strong></li>
            <li>AI may assist with communications (you can opt out)</li>
          </ul></div>
          <div class="hl-section"><div class="hl-section-title">YOUR PRIVACY &amp; TERMINATION</div><ul class="hl-list">
            <li>Access, copy, and request corrections to your records</li>
            <li>Request restrictions on use of your information</li>
            <li>Opt out of AI-assisted communications</li>
            <li>Either party may end the relationship per Section 8</li>
            <li>Governed by ${practiceState} law; arbitration in ${venue}</li>
          </ul></div>
        </div>
      </div>

      <p class="intro-text"><span class="hello">Hello!</span> Your treating physician, ${physicianName} of ${practiceName}, is committed to providing you with high-quality care. Please review the key terms of this Patient Care Agreement below.</p>

      <div class="agreement-section"><div class="section-header">1. PARTIES</div>
        <p class="agreement-text">This Agreement is entered into between <strong>${practiceName}</strong>, together with the treating physician <strong>${physicianName}</strong>, whose practice details are set out above (collectively, the "Physician" or "Practice"), and the individual identified above as the "Patient." The Physician is an independent, licensed medical professional who is solely responsible for all medical care provided to the Patient. This Agreement governs the professional relationship between the Patient and the Physician.</p>
      </div>

      <div class="agreement-section"><div class="section-header">2. ROLE OF ORELLA (TECHNOLOGY PLATFORM)</div>
        <p class="agreement-text">Orella, Inc. ("Orella") operates a technology platform that facilitates the connection between patients and independent physicians and supports administrative functions such as scheduling, intake, documentation, payments, and communications. <strong>Orella is not a healthcare provider and does not practice medicine, render diagnoses, or provide treatment. Orella does not employ, supervise, or direct the Physician and exercises no control over the Physician's independent clinical judgment.</strong> All medical care is provided solely by the Physician. Orella is not a party to the physician-patient relationship and, to the maximum extent permitted by law, assumes no responsibility or liability for the medical services provided by the Physician.</p>
      </div>

      <div class="agreement-section"><div class="section-header">3. SERVICES</div>
        <p class="agreement-text">The Physician agrees to provide the Patient with medical and non-medical services ("Services") within the Physician's scope of practice. The nature and extent of Services will be determined through consultation between the Physician and Patient, and may include:</p>
        <ul class="agreement-list">
          <li><strong>Specialist Consultation and Examination:</strong> subject to availability in your area.</li>
          <li><strong>Record of the Case:</strong> including findings, assessment, and treatment recommendations.</li>
          <li><strong>Prescriptions:</strong> for recommended or indicated studies, laboratory tests, medication, or consultations.</li>
        </ul>
        <p class="agreement-text">Hospital, emergency, radiology, third-party, and laboratory services are not included unless offered as a cash package at additional cost.</p>
      </div>

      <div class="agreement-section"><div class="section-header">4. INSURANCE</div>
        <p class="agreement-text"><strong>Authorization:</strong> To help ensure your visit is covered, please confirm with your insurance provider that you have authorization for the services being provided before your appointment. The specific procedure codes depend on the nature of your visit.</p>
        <p class="agreement-text"><strong>PPO:</strong> No authorization is required, but you may be responsible for a co-payment and/or deductible per your policy.</p>
        <p class="agreement-text"><strong>HMO:</strong> You must obtain a referral from your primary care physician before scheduling a consultation.</p>
        <p class="agreement-text"><strong>Patient Responsibility:</strong> While the Practice will bill your insurance, you are ultimately responsible for all costs not covered by your plan, including copayments, deductibles, and denied claims. The Practice will provide the documentation needed to submit to your insurer for reimbursement and will assist with the appeals process if needed. It is your responsibility to verify coverage for visits, procedures, and surgeries.</p>
      </div>

      <div class="agreement-section"><div class="section-header">5. COMMUNICATIONS</div>
        <p class="agreement-text"><strong>Contact:</strong> ${practiceName}, ${practiceAddress}${contactLine ? ` &nbsp;|&nbsp; ${contactLine}` : ''}</p>
        <p class="agreement-text"><strong>Preferred Methods:</strong> Please use email for general inquiries and the secure patient portal for sensitive information or medical records.</p>
        <p class="agreement-text"><strong>Email Confidentiality:</strong> Email is not entirely secure. The Practice will make every effort to protect your privacy but cannot guarantee the confidentiality of information sent via email.</p>
        <div class="callout emergency"><strong>EMERGENCIES:</strong> Email is not appropriate for emergencies or time-sensitive issues. In an emergency, call 911 or go to the nearest emergency room.</div>
        <p class="agreement-text"><strong>GenAI Disclosure:</strong> Where applicable, communications may be generated with the assistance of GenAI, and you will be notified as outlined in Section 22.</p>
      </div>

      <div class="agreement-section"><div class="section-header">6. DESIGNATED PHYSICIAN PROGRAM</div>
        <p class="agreement-text">Services will be personally provided by the Physician named in this Agreement. If the Physician is unavailable, an equally qualified covering physician or other licensed professional will be designated to attend to your care needs and will have access to your medical history and course of care.</p>
      </div>

      <div class="agreement-section"><div class="section-header">7. APPOINTMENT SCHEDULING</div>
        <p class="agreement-text">Appointments are scheduled on a first-come, first-served basis, unless the Physician determines that a patient's medical condition requires urgent attention.</p>
      </div>

      <div class="agreement-section"><div class="section-header">8. TERM AND TERMINATION</div>
        <p class="agreement-text">This Agreement remains in effect for as long as the Patient receives care from the Practice. It may be terminated as follows:</p>
        <p class="agreement-text"><strong>Patient:</strong> at any time following the completion of a consultation, or immediately if the Patient relocates outside the Practice's service area.</p>
        <p class="agreement-text"><strong>Practice:</strong> (a) if the Patient breaches this Agreement and fails to cure within ten days; (b) if the Patient carries an outstanding balance of $100 or greater and fails to pay within ten days of request; or (c) with thirty days' prior written notice, with or without cause (prepaid fees prorated and refunded). This Agreement automatically terminates upon the death or dissolution of either party.</p>
      </div>

      <div class="agreement-section"><div class="section-header">9. DISPUTE RESOLUTION</div>
        <p class="agreement-text">Please raise any concerns with the Practice first. If a resolution cannot be reached, the parties encourage mediation. If mediation is unsuccessful, any dispute arising under this Agreement will be submitted to binding arbitration under the rules of the American Arbitration Association in ${venue}.</p>
      </div>

      <div class="agreement-section"><div class="section-header">10. INDEPENDENT MEDICAL JUDGMENT</div>
        <p class="agreement-text">The Physician retains complete and free discretion to exercise their best professional medical judgment on the Patient's behalf. Nothing in this Agreement &mdash; and no service, tool, or communication provided through the Orella platform &mdash; shall be deemed to influence, limit, or affect the Physician's independent medical judgment.</p>
      </div>

      <div class="agreement-section"><div class="section-header">11. TERMS OF USAGE</div>
        <p class="agreement-text">The Practice may designate specific Terms of Usage as a supplement to this Agreement by providing written notice. In the event of any conflict, the Terms of Usage shall control.</p>
      </div>

      <div class="agreement-section"><div class="section-header">12. CHANGE OF LAW</div>
        <p class="agreement-text">If any change in state or federal law significantly impacts this Agreement, either party may request renegotiation. If an agreement cannot be reached within 30 days, either party may terminate this Agreement.</p>
      </div>

      <div class="agreement-section"><div class="section-header">13. SEVERABILITY</div>
        <p class="agreement-text">If any provision of this Agreement is deemed invalid or unenforceable, the remaining provisions shall remain in effect.</p>
      </div>

      <div class="agreement-section"><div class="section-header">14. NOTICE</div>
        <p class="agreement-text">Any written notice required under this Agreement shall be sent via facsimile, recognized overnight courier, or certified mail, return receipt requested, to the addresses indicated in this document.</p>
      </div>

      <div class="agreement-section"><div class="section-header">15. AMENDMENT</div>
        <p class="agreement-text">This Agreement may be amended by a written agreement signed by both parties. The Physician may unilaterally amend this Agreement to comply with applicable laws and regulations by providing 30 days' advance written notice to the Patient.</p>
      </div>

      <div class="agreement-section"><div class="section-header">16. ASSIGNMENT</div>
        <p class="agreement-text">This Agreement may not be assigned to another individual by the Patient.</p>
      </div>

      <div class="agreement-section"><div class="section-header">17. LEGAL SIGNIFICANCE</div>
        <p class="agreement-text">This Agreement is a legal document. You acknowledge that you have had the opportunity to seek legal advice and are satisfied with the terms and conditions.</p>
      </div>

      <div class="agreement-section"><div class="section-header">18. GOVERNING LAW / ARBITRATION</div>
        <p class="agreement-text">This Agreement shall be governed by the laws of the State of ${practiceState}. The parties waive any right to a trial by jury.</p>
      </div>

      <div class="agreement-section"><div class="section-header">19. WAIVER</div>
        <p class="agreement-text">The failure of a party to enforce any provision of this Agreement shall not be deemed a waiver of that provision.</p>
      </div>

      <div class="agreement-section"><div class="section-header">20. SERVICE</div>
        <p class="agreement-text">All written notices are deemed served if sent by first-class U.S. mail to the address on file with the Practice.</p>
      </div>

      <div class="agreement-section"><div class="section-header">21. ENTIRE AGREEMENT</div>
        <p class="agreement-text">This Agreement constitutes the entire agreement between the parties and supersedes any prior agreements or understandings regarding its subject matter.</p>
      </div>

      <div class="agreement-section"><div class="section-header">22. GENERATIVE ARTIFICIAL INTELLIGENCE (GENAI)</div>
        <div class="callout">
          <p class="agreement-text" style="margin: 0;">The Practice may utilize Generative Artificial Intelligence (GenAI) to assist in communicating patient clinical information. When GenAI is used to create communications containing your clinical information, you will be notified at the beginning of the communication. This notification does not apply to communications reviewed by a licensed or certified human healthcare provider. <strong>You have the right to request that your clinical information not be used with GenAI. Please inform the Practice if you wish to opt out.</strong></p>
        </div>
      </div>

      <div class="agreement-section"><div class="section-header">HIPAA NOTICE OF PRIVACY PRACTICES</div>
        <p class="agreement-text" style="font-size: 8.5pt; color: #5b6873;">Effective Date: March 1, 2025</p>
        <p class="agreement-text"><strong>THIS NOTICE DESCRIBES HOW MEDICAL INFORMATION ABOUT YOU MAY BE USED AND DISCLOSED AND HOW YOU CAN GET ACCESS TO THIS INFORMATION.</strong></p>
        <p class="agreement-text">The Practice is committed to protecting your privacy. This Notice outlines how ${practiceName} may use and disclose your Protected Health Information (PHI) to carry out treatment, payment, or health care operations and for other purposes permitted or required by law.</p>
        <p class="agreement-text"><strong>How We May Use Your PHI:</strong> Treatment (coordinating care, referrals), Payment (obtaining payment, determining eligibility), Health Care Operations (quality improvement, training, business functions).</p>
        <p class="agreement-text"><strong>Your Rights:</strong> to inspect and copy your records; to request restrictions; to confidential communications; to amend your records; to an accounting of disclosures; and to a paper copy of this Notice.</p>
        <p class="agreement-text"><strong>Filing a Complaint:</strong> If you believe your privacy rights have been violated, contact the Practice's Privacy Officer at ${practiceEmail || 'the Practice'} or the U.S. Department of Health and Human Services. The Practice will not retaliate against you for filing a complaint.</p>
      </div>

      <div class="issued-by">
        <div class="label">Issued by</div>
        <div class="name">${physicianName}</div>
        <div>${practiceName}</div>
      </div>

      <div class="sig-box">
        <div class="sig-title">Patient Acknowledgement &amp; Electronic Signature</div>
        <div class="sig-ack">By signing below, I acknowledge that I have read, understand, and agree to the terms of this Patient Care Agreement, and I acknowledge receipt of the Notice of Privacy Practices.</div>
        ${signatureData
          ? `<img src="${signatureData}" alt="Patient Signature" class="sig-image" />`
          : '<p style="color:#9a3a3a;font-size:10pt;">Signature pending</p>'}
        <div class="sig-line"><strong>Patient:</strong> ${patientName} &nbsp;&nbsp; <strong>Signed:</strong> ${signedDate || 'N/A'}</div>
      </div>

      <div class="version-stamp">Patient Care Agreement v3.0 &middot; Terms effective ${TERMS_EFFECTIVE} &middot; Signature bound to this version</div>

      <div class="document-footer">
        <div class="footer-left"><span>Powered by</span>${SMALL_LOGO_HTML}</div>
        <div>Confidential Medical Information</div>
      </div>
    </body>
    </html>
  `
}

// v225.3.0 (Phase B-2) — data-driven render from a sections snapshot.
// Used when an agreement was signed against an admin-published DB version
// (props.sections = [{heading, body}]). Reuses the V2 chrome so a web-link
// signature renders identically to an app signature, but with the exact
// sections the patient was shown (frozen on the signed record).
const esc = (s = '') => String(s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
const richText = (s = '') => esc(s).replace(/\n/g, '<br>')

const generateAgreementFromSections = ({
  patientName, patientDOB, patientSex, patientId, caseId,
  signatureData, signedDate, doctor = {},
  title = 'Patient Care Agreement', sections = [], agreementVersion,
  effectiveDate,
}) => {
  const physicianName = (doctor && doctor.firstName && doctor.lastName)
    ? `${doctor.firstName} ${doctor.lastName}, ${doctor.credentials || 'MD'}`
    : (doctor && doctor.physicianName) || 'Rami Abdou, MD'
  const practiceName    = (doctor && doctor.practiceName) || 'Dr. Rami MD Med Corp'
  const practiceAddress = (doctor && doctor.practiceAddress) || ''
  const practicePhone   = (doctor && doctor.practicePhone) || ''
  const practiceFax      = (doctor && doctor.practiceFax) || ''
  const practiceEmail   = (doctor && doctor.practiceEmail) || ''
  const orlId = patientId || (caseId ? caseId.slice(-8) : '') || 'N/A'
  const contactLine = [
    practicePhone && `Tel: ${practicePhone}`,
    practiceFax && `Fax: ${practiceFax}`,
    practiceEmail && `Email: ${practiceEmail}`,
  ].filter(Boolean).join(' &nbsp;&middot;&nbsp; ')
  const sectionsHtml = (Array.isArray(sections) ? sections : [])
    .filter(s => s && (s.heading || s.body))
    .map((s, i) => `
      <div class="agreement-section"><div class="section-header">${i + 1}. ${esc((s.heading || '').toUpperCase())}</div>
        <p class="agreement-text">${richText(s.body || '')}</p>
      </div>`).join('')

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>${esc(title)} - ${esc(patientName)}</title>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; padding: 32px 44px; background: #ffffff; color: #26313b; line-height: 1.55; font-size: 12pt; }
        .document-title { text-align: center; margin: 4px 0 6px; }
        .document-title h1 { font-size: 17pt; font-weight: 700; color: #15354F; letter-spacing: 1px; }
        .title-rule { height: 2px; background: #15354F; width: 100%; margin: 0 0 16px; }
        .info-box { background: #F7F9FB; border: 1px solid #D6DCE2; border-radius: 4px; padding: 11px 14px; margin-bottom: 10px; }
        .info-row { display: flex; justify-content: space-between; flex-wrap: wrap; gap: 10px; }
        .info-item { flex: 1; min-width: 120px; }
        .info-label { font-size: 9pt; color: #5b6873; text-transform: uppercase; font-weight: 600; letter-spacing: .4px; margin-bottom: 1px; }
        .info-value { font-size: 12pt; color: #15354F; font-weight: 700; }
        .practice-box { background: #F7F9FB; border: 1px solid #D6DCE2; border-radius: 4px; padding: 12px 16px; margin-bottom: 18px; }
        .pb-grid { display: grid; grid-template-columns: 155px 1fr; row-gap: 5px; column-gap: 14px; font-size: 11pt; }
        .pb-label { color: #5b6873; font-weight: 600; }
        .pb-value { color: #15354F; font-weight: 600; }
        .intro-text { font-size: 12pt; color: #3b4754; margin-bottom: 14px; }
        .intro-text .hello { font-weight: 700; color: #15354F; }
        .agreement-section { margin-bottom: 13px; page-break-inside: avoid; }
        .section-header { font-size: 11pt; font-weight: 700; color: #15354F; margin-bottom: 4px; border-bottom: 1px solid #E2E8F0; padding-bottom: 2px; }
        .agreement-text { font-size: 11.5pt; line-height: 1.55; color: #3b4754; margin-bottom: 6px; text-align: justify; }
        .issued-by { margin-top: 16px; padding-top: 10px; border-top: 1px solid #E2E8F0; }
        .issued-by .label { font-size: 9pt; text-transform: uppercase; letter-spacing: .5px; color: #5b6873; font-weight: 600; margin-bottom: 4px; }
        .issued-by .name { font-weight: 700; color: #15354F; }
        .sig-box { background: #F7F9FB; border: 1px solid #D6DCE2; border-top: 2px solid #15354F; border-radius: 4px; padding: 14px 16px; margin-top: 16px; }
        .sig-title { font-size: 10.5pt; font-weight: 700; color: #15354F; text-transform: uppercase; margin-bottom: 6px; letter-spacing: .4px; }
        .sig-ack { font-size: 10.5pt; color: #3b4754; margin-bottom: 10px; }
        .sig-image { max-height: 48px; margin-bottom: 4px; }
        .sig-line { font-size: 10.5pt; color: #26313b; }
        .version-stamp { margin-top: 10px; font-size: 8.5pt; color: #94A3B8; text-align: center; }
        .document-footer { margin-top: 22px; padding-top: 10px; border-top: 1px solid #E2E8F0; display: flex; justify-content: space-between; align-items: center; font-size: 8.5pt; color: #5b6873; }
        .footer-left { display: flex; align-items: center; gap: 7px; }
        @media print { body { padding: 18px 26px; } .agreement-section { page-break-inside: avoid; } }
      </style>
    </head>
    <body>
      <div class="document-title"><h1>${esc((title || 'PATIENT CARE AGREEMENT').toUpperCase())}</h1></div>
      <div class="title-rule"></div>

      <div class="info-box"><div class="info-row">
        <div class="info-item"><div class="info-label">Patient Name</div><div class="info-value">${esc(patientName)}</div></div>
        <div class="info-item"><div class="info-label">Date of Birth</div><div class="info-value">${esc(patientDOB || 'N/A')}</div></div>
        <div class="info-item"><div class="info-label">Sex</div><div class="info-value">${esc(patientSex || 'N/A')}</div></div>
        <div class="info-item"><div class="info-label">ORL #</div><div class="info-value">${esc(orlId)}</div></div>
      </div></div>

      <div class="practice-box">
        <div class="pb-grid">
          <div class="pb-label">Practice</div><div class="pb-value">${esc(practiceName)}</div>
          <div class="pb-label">Treating Physician</div><div class="pb-value">${esc(physicianName)}</div>
          ${practiceAddress ? `<div class="pb-label">Address</div><div class="pb-value">${esc(practiceAddress)}</div>` : ''}
          ${contactLine ? `<div class="pb-label">Contact</div><div class="pb-value">${contactLine}</div>` : ''}
        </div>
      </div>

      <p class="intro-text"><span class="hello">Hello!</span> Your treating physician, ${esc(physicianName)} of ${esc(practiceName)}, is committed to providing you with high-quality care. Please review the terms of this ${esc(title)} below.</p>

      ${sectionsHtml}

      <div class="issued-by">
        <div class="label">Issued by</div>
        <div class="name">${esc(physicianName)}</div>
        <div>${esc(practiceName)}</div>
      </div>

      <div class="sig-box">
        <div class="sig-title">Patient Acknowledgement &amp; Electronic Signature</div>
        <div class="sig-ack">By signing below, I acknowledge that I have read, understand, and agree to the terms of this ${esc(title)}, and I acknowledge receipt of the Notice of Privacy Practices.</div>
        ${signatureData
          ? `<img src="${signatureData}" alt="Patient Signature" class="sig-image" />`
          : '<p style="color:#9a3a3a;font-size:10pt;">Signature pending</p>'}
        <div class="sig-line"><strong>Patient:</strong> ${esc(patientName)} &nbsp;&nbsp; <strong>Signed:</strong> ${esc(signedDate || 'N/A')}</div>
      </div>

      <div class="version-stamp">${esc(title)}${agreementVersion ? ` v${esc(String(agreementVersion))}` : ''}${effectiveDate ? ` &middot; effective ${esc(effectiveDate)}` : ''} &middot; Signature bound to this version</div>

      <div class="document-footer">
        <div class="footer-left"><span>Powered by</span>${SMALL_LOGO_HTML}</div>
        <div>Confidential Medical Information</div>
      </div>
    </body>
    </html>
  `
}


// ═══════════════════════════════════════════════════════
// s101 (Dr. Rami 2026-10-12/13) — ORELLA PLATFORM AGREEMENT. Replaces the Patient Care Agreement
// for every NEW signature. Ruling: the agreement covers Orella and its role only; patients still
// sign their own physician's agreement; billing is between patient and MD.
// ★★★ THE EDITABLE COPY LIVES IN THE ADMIN PANEL (Compliance › Patient agreement, the GLOBAL
//   agreement_templates row). A new signature binds and snapshots the active global template when
//   its title names the Platform Agreement; until one is published, the default below
//   (PLATFORM_AGREEMENT_SECTIONS = claude/ORELLA_PLATFORM_AGREEMENT_DRAFT_v2.md) is signed, under
//   the marker hardcodedTemplate 'v4.0'. The admin panel seeds its editor from the same text.
// ★★ ONE LAYOUT: an Orella document, no practice block, no physician, no fees. A body line that
//   starts with "- " is a bullet; **text** is bold. V1-V3 stay byte-identical; a record renders
//   the version it carries (isPlatformAgreement decides the layout).
// ═══════════════════════════════════════════════════════
export const PLATFORM_AGREEMENT_TITLE = 'Orella Platform Agreement'
export const PLATFORM_AGREEMENT_EFFECTIVE = '2026-10-13'
export const PLATFORM_AGREEMENT_SECTIONS = [
  { heading: 'Before you start', body: 'Orella helps you organize your health concern, share it with a doctor, and follow your care plan. **Orella is not your doctor and does not provide medical care.** Your doctor is responsible for your diagnosis and treatment.\n**In an emergency, call 911 or go to the nearest emergency room. Do not use Orella.**' },
  { heading: 'What Orella does', body: '- Orella is a technology platform made by Orella, Inc.\n- Orella asks you questions, helps you collect photos and documents, and writes a summary of what you shared. It can show you conditions that resemble your symptoms and warning signs to watch for. This is to help you prepare. **It is not a diagnosis.**\n- Orella lets you send your case to a doctor you choose, by secure link, by fax to their office, or directly in the app if they use Orella.\n- Orella shows you your doctor\'s instructions, orders and appointments, and reminds you about them.' },
  { heading: 'Your doctor', body: '- The doctor or practice you choose is **independent of Orella**. Orella does not employ, supervise or direct them, and does not decide your care.\n- Your doctor\'s office may ask you to sign **their own** patient agreement and consent forms. Those govern your care with them.\n- If a doctor declines your case or doesn\'t respond, you can choose another doctor. Orella does not guarantee that a particular doctor will see you.' },
  { heading: 'Costs and payment', body: '- **Orella is free for patients.** Orella does not bill you for medical care.\n- Fees, co-pays, deductibles and payment for your visit are between you and your doctor\'s office.\n- With your permission, Orella may check your insurance coverage with your insurer, at no cost to you, to show you your benefits and help your doctor\'s office. That information comes from your insurer and may be incomplete. Your doctor\'s office and your insurer have the final word.\n- If Orella ever offers a paid feature, its price will be shown before you agree to it.' },
  { heading: 'Emergencies and urgent symptoms', body: '- Orella is **not monitored in real time** and is not for emergencies or time-sensitive problems.\n- If one of your answers suggests something urgent, Orella will tell you right away and flag it on your case. Your doctor sees that flag only after your case has been shared with them.' },
  { heading: 'Your information', body: '- Orella protects your health information as required by HIPAA. When your doctor uses Orella, Orella handles your information on their behalf under a Business Associate Agreement with your doctor\'s practice.\n- **You decide who receives your case.** Orella shares it only with the doctor or office you choose, and with the service providers Orella needs to run, such as secure hosting, fax delivery, text and email delivery and insurance checks, under agreements that require them to protect it.\n- You can stop a share from the app at any time. An office that already received a fax keeps that copy.\n- Summaries and suggestions in Orella are produced with the help of AI. They become part of your care only after your doctor reviews them.\n- Our Privacy Policy explains how to see, download or delete your information.' },
  { heading: 'Messages from Orella', body: '- By using Orella you agree to receive messages about your case by app notification and email, and by text message if you give us your mobile number. These include verification codes, appointment confirmations and reminders, and care-plan alerts. Message frequency varies. Message and data rates may apply. Reply STOP to stop text messages. You can change your choices in Settings.\n- Messages in Orella are not for emergencies.' },
  { heading: 'Video visits', body: '- If your doctor offers a video visit, it takes place through a secure third-party video service linked from Orella.\n- A video visit is held to the same standard of care as an in-person visit. Your doctor decides whether a video visit is appropriate for you and may ask you to come in person instead.\n- You can ask for an in-person visit instead at any time.\n- Use a private place for the visit. Your doctor may ask you to show the area of concern on camera.\n- Your doctor\'s office may ask for its own telehealth consent, as required in your state.' },
  { heading: 'Your choices', body: '- You can stop using Orella at any time and delete your account from Settings.\n- Deleting your Orella account does not delete records your doctor\'s office keeps under its own obligations.' },
  { heading: 'Changes and questions', body: '- If this agreement changes, we\'ll ask you to review it again before you continue.\n- Questions: **admin@orellahealth.com**.\n- Orella\'s Terms of Use also apply.' },
]
/** A record or template is the Platform Agreement: its own scope, the v4 marker, or its title. */
export const isPlatformAgreement = (p) => !!p && (p.scope === 'platform' || p.hardcodedTemplate === 'v4.0'
  || /orella platform agreement/i.test(String(p.title || '')))
const generatePlatformAgreement = ({ patientName, patientDOB, patientSex, patientId, caseId, signatureData, signedDate, title, sections, agreementVersion, effectiveDate }) => {
  const e4 = (v) => String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
  const rich = (t) => e4(t).replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  const docTitle = String(title || PLATFORM_AGREEMENT_TITLE)
  const list = Array.isArray(sections) && sections.length ? sections : PLATFORM_AGREEMENT_SECTIONS
  const effRaw = String(effectiveDate || PLATFORM_AGREEMENT_EFFECTIVE)
  const effM = /^(\d{4})-(\d{2})-(\d{2})/.exec(effRaw)
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
  const eff = effM && Number(effM[2]) >= 1 && Number(effM[2]) <= 12 ? `${MONTHS[Number(effM[2]) - 1]} ${Number(effM[3])}, ${effM[1]}` : effRaw
  const ver = agreementVersion ? String(agreementVersion) : '4'
  const orlId = patientId || (caseId ? String(caseId).slice(-8) : '') || 'N/A'
  const bodyHtml = (b) => {
    const lines = String(b || '').split('\n').map((x) => x.trim()).filter(Boolean)
    let out = '', items = []
    const flush = () => { if (items.length) { out += `<ul class="agreement-list">${items.map((i) => `<li>${rich(i)}</li>`).join('')}</ul>`; items = [] } }
    for (const l of lines) { if (l.startsWith('- ')) items.push(l.slice(2)); else { flush(); out += `<p class="agreement-text">${rich(l)}</p>` } }
    flush()
    return out
  }
  const sectionsHtml = list.filter((s) => s && (s.heading || s.body)).map((s, i) =>
    `<div class="agreement-section"><div class="section-header">${i + 1}. ${e4(s.heading || '')}</div>${bodyHtml(s.body)}</div>`).join('')
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>${e4(docTitle)} - ${e4(patientName)}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', sans-serif; padding: 32px 44px; background: #ffffff; color: #26313b; line-height: 1.55; font-size: 12pt; }
        .document-title { text-align: center; margin: 4px 0 6px; }
        .document-title h1 { font-size: 17pt; font-weight: 700; color: #15354F; letter-spacing: 1px; text-transform: uppercase; }
        .document-title p { font-size: 10pt; color: #5b6873; }
        .title-rule { height: 2px; background: #15354F; width: 100%; margin: 0 0 16px; }
        .info-box { background: #F7F9FB; border: 1px solid #D6DCE2; border-radius: 4px; padding: 11px 14px; margin-bottom: 16px; }
        .info-row { display: flex; justify-content: space-between; flex-wrap: wrap; gap: 10px; }
        .info-item { flex: 1; min-width: 120px; }
        .info-label { font-size: 9pt; color: #5b6873; text-transform: uppercase; font-weight: 600; letter-spacing: .4px; margin-bottom: 1px; }
        .info-value { font-size: 12pt; color: #15354F; font-weight: 700; }
        .agreement-section { margin-bottom: 13px; page-break-inside: avoid; }
        .section-header { font-size: 11pt; font-weight: 700; color: #15354F; margin-bottom: 4px; border-bottom: 1px solid #E2E8F0; padding-bottom: 2px; text-transform: uppercase; }
        .agreement-text { font-size: 11.5pt; line-height: 1.55; color: #3b4754; margin-bottom: 6px; }
        .agreement-text strong, .agreement-list strong { color: #26313b; }
        .agreement-list { margin: 4px 0 6px 20px; font-size: 11.5pt; color: #3b4754; }
        .agreement-list li { margin-bottom: 3px; }
        .sig-box { background: #F7F9FB; border: 1px solid #D6DCE2; border-top: 2px solid #15354F; border-radius: 4px; padding: 14px 16px; margin-top: 16px; }
        .sig-title { font-size: 10.5pt; font-weight: 700; color: #15354F; text-transform: uppercase; margin-bottom: 6px; letter-spacing: .4px; }
        .sig-ack { font-size: 10.5pt; color: #3b4754; margin-bottom: 10px; }
        .sig-image { max-height: 48px; margin-bottom: 4px; }
        .sig-line { font-size: 10.5pt; color: #26313b; }
        .version-stamp { margin-top: 10px; font-size: 8.5pt; color: #94A3B8; text-align: center; }
        .document-footer { margin-top: 22px; padding-top: 10px; border-top: 1px solid #E2E8F0; display: flex; justify-content: space-between; align-items: center; font-size: 8.5pt; color: #5b6873; }
        @media print { body { padding: 18px 26px; } }
      </style>
    </head>
    <body>
      <div class="document-title"><h1>${e4(docTitle)}</h1><p>Orella, Inc. &middot; effective ${e4(eff)}</p></div>
      <div class="title-rule"></div>
      <div class="info-box"><div class="info-row">
        <div class="info-item"><div class="info-label">Patient Name</div><div class="info-value">${e4(patientName)}</div></div>
        <div class="info-item"><div class="info-label">Date of Birth</div><div class="info-value">${e4(patientDOB || 'N/A')}</div></div>
        <div class="info-item"><div class="info-label">Sex</div><div class="info-value">${e4(patientSex || 'N/A')}</div></div>
        <div class="info-item"><div class="info-label">ORL #</div><div class="info-value">${e4(orlId)}</div></div>
      </div></div>
      ${sectionsHtml}
      <div class="sig-box">
        <div class="sig-title">Patient Acknowledgement &amp; Electronic Signature</div>
        <div class="sig-ack">I have read and agree to the ${e4(docTitle)}.</div>
        ${signatureData
          ? `<img src="${signatureData}" alt="Patient Signature" class="sig-image" />`
          : '<p style="color:#9a3a3a;font-size:10pt;">Signature pending</p>'}
        <div class="sig-line"><strong>Patient:</strong> ${e4(patientName)} &nbsp;&nbsp; <strong>Signed:</strong> ${e4(signedDate || 'N/A')}</div>
      </div>
      <div class="version-stamp">${e4(docTitle)} v${e4(ver)} &middot; effective ${e4(eff)} &middot; Signature bound to this version</div>
      <div class="document-footer"><div>Orella, Inc. &middot; admin@orellahealth.com</div><div>Confidential</div></div>
    </body>
    </html>
  `
}
// The v4.0 marker renders the default text (a record without a snapshot).
const generateAgreementV4 = (p) => generatePlatformAgreement(p)
// s101 (D496): the Orella signature page for a physician agreement signed as the physician's OWN
// PDF. The PDF is shown as uploaded; this page records who signed which file and version, when.
export const generatePracticeSignaturePage = ({ patientName, patientDOB, title, fileName, version, provider, signatureData, signedDate }) => {
  const e5 = (v) => String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Signature page - ${e5(title)}</title>
      <style>
        body { font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', sans-serif; padding: 40px 48px; color: #26313b; font-size: 12pt; line-height: 1.55; }
        h1 { font-size: 16pt; color: #15354F; text-transform: uppercase; letter-spacing: 1px; margin: 0 0 4px; }
        .sub { color: #5b6873; font-size: 10pt; margin-bottom: 18px; }
        .box { border: 1px solid #D6DCE2; border-top: 2px solid #15354F; border-radius: 4px; padding: 16px; background: #F7F9FB; }
        .row { display: flex; justify-content: space-between; gap: 12px; padding: 4px 0; font-size: 11pt; }
        .row b { color: #15354F; }
        .sig { max-height: 56px; margin: 10px 0 4px; }
        .foot { margin-top: 18px; font-size: 8.5pt; color: #94A3B8; text-align: center; }
      </style>
    </head>
    <body>
      <h1>Signature page</h1>
      <div class="sub">${e5(title)}${provider ? ` &middot; ${e5(provider)}` : ''}</div>
      <div class="box">
        <div class="row"><span>Patient</span><b>${e5(patientName)}</b></div>
        <div class="row"><span>Date of birth</span><b>${e5(patientDOB || 'N/A')}</b></div>
        <div class="row"><span>Document</span><b>${e5(fileName || 'Agreement.pdf')}</b></div>
        <div class="row"><span>Version</span><b>v${e5(version || '1')}</b></div>
        <p style="margin:12px 0 4px">I have read and agree to the ${e5(title)} named above.</p>
        ${signatureData ? `<img class="sig" src="${signatureData}" alt="Patient signature" />` : '<p style="color:#9a3a3a">Signature pending</p>'}
        <div class="row"><span>Signed</span><b>${e5(signedDate || 'N/A')}</b></div>
      </div>
      <div class="foot">Signed electronically through Orella together with the Orella Platform Agreement &middot; bound to this version</div>
    </body>
    </html>
  `
}

// v224.7.0 — version-aware dispatcher. agreementVersion >= 2 renders the new
// generalized template; absent/1 renders the legacy template unchanged, so
// agreements signed before this release are never revised.
export const generateAgreementPDF = (props = {}) => {
  // v225.3.0: a signed record carrying a sections snapshot (web-link signatures,
  // and future DB-bound app signatures) renders data-driven from that snapshot,
  // so the document shows exactly what the patient was presented. Absent a
  // snapshot, fall back to the version-aware hardcoded templates unchanged.
  // ★★★ s101: the Orella Platform Agreement first - the admin-published GLOBAL template's snapshot
  //   or, without one, the default text (marker 'v4.0'). Its layout never shows a practice block.
  if (isPlatformAgreement(props)) return generatePlatformAgreement(props)
  if (Array.isArray(props.sections) && props.sections.length > 0) {
    return generateAgreementFromSections(props)
  }
  // ★★★ P2 repair 2 (NIT-1): V3 ONLY for a V3 signature of the hardcoded template, by its explicit
  //   marker. agreementVersion cannot say it: the hardcoded numbers share their numbering with the
  //   admin-published agreement_templates.version, so a web-link record bound to DB version 3+ with
  //   an empty sections snapshot carries agreementVersion >= 3 and rendered V2 before P2 - it must
  //   keep rendering V2 (no signed document may change). Every pre-P2 record has no marker and
  //   takes the a0a93405 path below unchanged.
  // s101: V4 (the Orella Platform Agreement) by its own marker; V3 keeps its own.
  if (props && props.hardcodedTemplate === CURRENT_HARDCODED_TEMPLATE) return generateAgreementV4(props)
  if (props && props.hardcodedTemplate === HARDCODED_V3_TEMPLATE) return generateAgreementV3(props)
  const v = Number(props && props.agreementVersion) || 1
  return v >= 2 ? generateAgreementV2(props) : generateAgreementV1(props)
}

// v242.239.0 (TEST 2.21): the hardcoded version a NEW app signature records (and its preview shows)
// when no admin-published version is active. Older records keep the version they were signed on.
// s101: a new signature records v4, the Orella Platform Agreement.
export const CURRENT_AGREEMENT_VERSION = 4
// P2 repair 2 (NIT-1): the marker a new app signature of the hardcoded V3 text records
// (patientAgreement.hardcodedTemplate), whatever agreementVersion it binds; it matches V3's stamp.
export const CURRENT_HARDCODED_TEMPLATE = 'v4.0'
// The V3 (Patient Care Agreement, no pricing) marker: records signed under it keep rendering V3.
export const HARDCODED_V3_TEMPLATE = 'v3.0'
